/**
 * roles.js — pure helpers for role-based access checks.
 *
 * Backend exposes `role` on the auth user (master | owner | member) plus
 * `tenant_id` for multi-tenancy. These helpers centralize role checks so
 * components don't compare strings inline.
 *
 * Role model after PR-2 rename: {master, owner, member}.
 * (owner = former "operator"; member = former "viewer")
 *
 * All helpers are null-safe: a missing/undefined user returns `false`.
 */

export const ROLES = Object.freeze({
  MASTER: "master",
  OWNER: "owner",
  MEMBER: "member",
});

export const ALL_ROLES = [ROLES.MASTER, ROLES.OWNER, ROLES.MEMBER];

/**
 * Rótulo en es-CO de cada rol. Vivía duplicado en `UsersSection`; la ficha de
 * una cuenta necesita el mismo, y dos mapas es como se termina rotulando
 * "Usuario" en una pantalla y "Operador" en la de al lado.
 */
export const ROLE_LABELS = Object.freeze({
  [ROLES.MASTER]: "Master",
  [ROLES.OWNER]: "Usuario",
  [ROLES.MEMBER]: "Subusuario",
});

export function isMaster(user) {
  return user?.role === ROLES.MASTER;
}

export function isOwner(user) {
  return user?.role === ROLES.OWNER;
}

export function isMember(user) {
  return user?.role === ROLES.MEMBER;
}

// Capacidades agregadas — usar estas en vez de comparar roles directamente.
export function canManageUsers(user) {
  // PR-A baseline: master-only user creation. PR-A.6 will widen.
  return user?.role === ROLES.MASTER;
}

export function canManageTenants(user) {
  return user?.role === ROLES.MASTER;
}

export function canManageFleet(user) {
  return [ROLES.MASTER, ROLES.OWNER].includes(user?.role);
}

export function isEndUser(user) {
  return user?.role === ROLES.MEMBER;
}

export function hasAnyRole(user, roles) {
  return Array.isArray(roles) && roles.includes(user?.role);
}
